import {get, indexOf, isArray, isEmpty, isString, map, reduce, set} from 'lodash'
import React from "react";
import {changePasswordApi, loginWithTokenAPI, otpLoginAPI, userLogin, userSSoTokenLogin, validate2fa, getUserApproverSignDocsAPI} from "../Services/api";
import {
    calcAndGetTokenFromLogin,
    emptyLoginStateFromLocalStorage,
    exitImpersonationSession,
    getDecodedToken,
    loadUserStateFromLocalStorage,
    setUserState
} from "../Utils/User/user";
import {deleteAccessToken, getAccessToken, setAccessToken} from "../Services/auth";
import {defaultLanguage, languagesMap} from './ContextLib';
import {SA_getPartnersClientSecretStatus, SA_logoutAsUserAPI} from "../Services/SA_api";
import { SignDocTypes } from '../Components/SignDoc/SignDocLib';


//context definition
export const UserReactContext = React.createContext({
    userState: {},
    redirect: false,
    user: {},
    daysUntilLicenseExpired: {},
    hasUserPendingApprovals: false,
    hasDocumentApproverAccess: false,
    hasCourseApproverAccess: false,
    hasExpiringPartnerSecret: false,
    login: async () => {
    },
    otpLogin: async () => {
    },
    loginWithToken: async () => {
    },
    logout: () => {
    },
    saveLogin: () => {
    },
    loadSavedToken: () => {
    },
    isUserValid: () => {
    },
    canUserPerformAction: () => {
    },
    isPermissionsLoaded: () => {
    },
    isUserSuperAdmin: () => {
    },
    isUserCompanyAdmin: () => {
    },
    changePassword: () => {
    },
    isManagingCompany: () => {
    },
    changeUserLanguage: ({language}) => {
    },
    getUserName: () => {
    },
    getUserCompanyId: () => {
    },
    validateMFA: () => {
    },
    getPreferences: ({attributes}) => {
    },
    getFirstLoginDate: async () => {},
    loginWithSSOToken: () => {},
    isUserApprover: () => {},
    isDocumentApprover: () => {},
    isCourseApprover: () => {},
    checkUserApprovals: async () => {},
    checkExpiringPartnerSecrets: async () => {}
})


export class UserContextProvider extends React.Component {
    constructor(props) {
        super(props)
        const userState = loadUserStateFromLocalStorage()
        this.state = {
            userState: userState,
            user: get(userState, 'user'),
            hasUserPendingApprovals: false,
            hasDocumentApproverAccess: false,
            hasCourseApproverAccess: false,
            hasExpiringPartnerSecret: false,
            login: this.login,
            otpLogin: this.otpLogin,
            loginWithToken: this.loginWithToken,
            logout: this.logout,
            saveLogin: this.saveLogin,
            loadSavedToken: this.loadSavedToken,
            validateMFA: this.validateMFA,
            isUserValid: this.isUserValid,
            changePassword: this.changePassword,
            isPermissionsLoaded: this.isPermissionsLoaded,
            redirect: false,
            canUserPerformAction: this.canUserPerformAction,
            daysUntilLicenseExpired: null,
            isUserSuperAdmin: this.isUserSuperAdmin,
            getUserName: this.getUserName,
            isManagingCompany: this.isManagingCompany,
            getUserCompanyId: this.getUserCompanyId,
            getPreferences: this.getPreferences,
            getFirstLoginDate: this.getFirstLoginDate,
            isUserCompanyAdmin: this.isUserCompanyAdmin,
            changeUserLanguage: this.changeUserLanguage,
            loginWithSSOToken: this.loginWithSSOToken,
            isUserApprover: this.isUserApprover,
            isDocumentApprover: this.isDocumentApprover,
            isCourseApprover: this.isCourseApprover,
            checkUserApprovals: this.checkUserApprovals,
            checkExpiringPartnerSecrets: this.checkExpiringPartnerSecrets
        }
        setAccessToken(getDecodedToken(get(userState, 'token')))
    }

    componentDidMount() {
        const {userState} = this.state;
        if (!isEmpty(get(userState, 'user'))) {
            // this.getUserAllowedActions(userState)
            // this.getLicenseExpiration()
        }
    }

    getFirstLoginDate = async () => {
        let firstLoginDate = get(this.state, 'user.loginData.firstLoginDate', null)
        if (!firstLoginDate) {
            set(this.state, 'user.loginData.firstLoginDate', new Date())
            setUserState({user: get(this.state, 'user'), token: get(this.state, 'user.token')})
            return null;
        }
        return firstLoginDate;
    }

    changeUserLanguage = async ({language}) => {
        if (!languagesMap[language]) {
            language = defaultLanguage
        }
        set(this.state, 'user.preferences.language', language)
        setUserState({user: get(this.state, 'user'), token: get(this.state, 'user.token')})
    }

    login = async ({identifier, password}) => {
        let userLoginResponse = await userLogin({identifier, password})
        if (get(userLoginResponse, 'user.mfa')) {
            this.saveLogin({loginData: get(userLoginResponse, 'user')})
            if(get(userLoginResponse, 'user.linkLogin')){
                return {linkLogin: true, mfa: true}
            }
            else{
                return {mfa: true}
            }
        } else {
            this.saveLogin({loginData: get(userLoginResponse, 'user')})
            return {}
        }
    };

    /**
     * OTP login method - allows users to login using only email/phone without password
     * 
     * @param {object} params - The function parameters container object
     * @param {string} params.contact - The contact information (email or phone)
     * @param {string} params.deliveryMethod - The delivery method ('email' or 'phone')
     * @param {string} [params.phoneCountryCode] - Country code for phone login (e.g., 'IL', 'US')
     * @param {string} [params.companyId] - Optional company ID for when user has selected a company
     * @returns {Promise<object>} Response with MFA info or company selection required
     */
    otpLogin = async ({contact, deliveryMethod, phoneCountryCode, companyId}) => {
        let otpLoginResponse = await otpLoginAPI({contact, deliveryMethod, phoneCountryCode, companyId})
        
        if (get(otpLoginResponse, 'user.mfa')) {
            // OTP was sent, user needs to verify code
            this.saveLogin({loginData: get(otpLoginResponse, 'user')})
            return {
                mfa: true,
                userId: get(otpLoginResponse, 'userId'),
                user: get(otpLoginResponse, 'user')
            }
        } else if (get(otpLoginResponse, 'requiresCompanySelection')) {
            // User belongs to multiple companies
            return {
                requiresCompanySelection: true,
                companies: get(otpLoginResponse, 'companies', [])
            }
        }
        
        return otpLoginResponse
    };

    loginWithSSOToken = async ({ssoToken}) => {
        let userLoginResponse = await userSSoTokenLogin({ssoToken})
        this.saveLogin({loginData: get(userLoginResponse, 'user')})
        return {}
    }
    loginWithToken = async ({token}) => {
        let userLoginResponse = await loginWithTokenAPI({token})
        this.saveLogin({loginData: get(userLoginResponse, 'user')})
        return {mfa: true}
    }
    getPreferences = ({attributes}) => {
        const preferences = get(this.state, 'user.preferences', {})
        //only return attributes listed in attributes array
        return attributes.reduce((acc, attribute) => {
            acc[attribute] = get(preferences, attribute)
            return acc
        }, {})
    }

    validateMFA = async ({code}) => {
        try {
            const userLoginResponse = await validate2fa({code})
            if (userLoginResponse.error) {
                return {error: userLoginResponse.error}
            }
            this.saveLogin({loginData: get(userLoginResponse, 'user')})
        } catch (e) {
            return {error: get(e, 'errors[0].message')}
        }
    }


    getUserName = () => {
        return get(this.state, 'user.firstName') + ' ' + get(this.state, 'user.lastName')
    }

    isManagingCompany = () => {
        return get(this.state, 'user.isCompanyAdmin', false)
    }

    getUserCompanyId = () => {
        return get(this.state, 'user.companyId')
    }


    logout = async ({noRedirect, callBack} = {}) => {
        const isImpersonating = get(this.state, 'user.isSuperAdminLogin', false);

        if (isImpersonating) {
            // Log super admin logout to backend for audit trail
            const superAdminLoginMeta = get(this.state, 'user.superAdminLoginMeta');

            if (superAdminLoginMeta) {
                try {
                    await SA_logoutAsUserAPI({
                        tokenId: superAdminLoginMeta.tokenId,
                        targetUserId: get(this.state, 'user.id'),
                        superAdminUserId: superAdminLoginMeta.superAdminUserId,
                        companyId: superAdminLoginMeta.companyId
                    });
                    console.log('Super admin logout logged successfully');
                } catch (error) {
                    console.error('Failed to log super admin logout:', error);
                    // Continue with logout even if API call fails
                }
            }

            // Exit impersonation mode - return to super admin session
            exitImpersonationSession();

            // Redirect to super admin page and reload to load super admin session
            window.location.href = '/nihol/users';

            // Note: No need to call callBack or update state because we're redirecting
        } else {
            // Complete logout - clear all sessions
            emptyLoginStateFromLocalStorage();
            this.setState({
                userState: null,
                user: null
            }, callBack);
            deleteAccessToken();
        }
    };

    saveLogin = ({loginData, decodedToken, withRedirect, allowedAction}) => {
        const token = calcAndGetTokenFromLogin(loginData, decodedToken)
        setUserState({user: loginData, token})
        this.setState({
            userState: loginData,
            user: loginData,
            redirect: withRedirect
        }, () => {
            if (withRedirect) {
                this.setState({
                    redirect: false
                })
            }
        })
    };
    isPermissionsLoaded = () => {
        return !isEmpty(get(this.state.userState, 'user.allowedSystemActions'))
    }
    canUserPerformAction = (actionStrings, operation = 'and') => {
        let res;
        if (!isArray(actionStrings) && isString(actionStrings)) {
            actionStrings = [actionStrings]
        }
        let values = map(actionStrings, (actionString) => {
            return indexOf(get(this.state, 'user.allowedSystemActions'), actionString) !== -1
        })
        if (operation === 'and') {
            res = reduce(values, (res, val) => {
                return res && val
            })
        } else if (operation === 'or') {
            res = reduce(values, (res, val) => {
                return res || val
            })
        }
        return res
    }
    loadSavedToken = () => {
        return loadUserStateFromLocalStorage().token;
    };


    isUserValid = () => {
        if (get(this.state, 'userState.mfa') || get(this.state, 'userState.user.mfa')) {
            return false
        }
        return getAccessToken()
    }
    isUserSuperAdmin = () => {
        return get(this.state, 'user.roles.SuperAdmin')
    }
    isUserCompanyAdmin = () => {
        return get(this.state, 'user.isCompanyAdmin')
    }
    changePassword = async ({oldPassword, newPassword}) => {
        let userLoginResponse = await changePasswordApi({oldPassword, newPassword})
        if (get(userLoginResponse, "error", null)){
            return Promise.reject(new Error(userLoginResponse.errorMessage))
        }
        this.saveLogin({userLoginResponse})
    }

    /**
     * Checks which SignDoc types the user is authorized to approve
     * Uses the new getUserApproverTypesAPI which queries the denormalized signDocType column
     * @returns {Promise<boolean>} True if user is an approver (for any type), false otherwise
     */
    checkUserApprovals = async () => {
        try {
            // Get approver types from backend (fast query using denormalized column)
            const result = await getUserApproverSignDocsAPI();
            const hasDocumentAccess = result.some(signDoc => signDoc.signDocType === SignDocTypes.UPLOAD_DOCUMENT);
            const hasCourseAccess = result.some(signDoc => signDoc.signDocType === SignDocTypes.FRONTAL_COURSE || signDoc.signDocType === SignDocTypes.ONLINE_COURSE);
            const hasAnyAccess = hasDocumentAccess || hasCourseAccess || this.isUserCompanyAdmin();
            this.setState({
                hasUserPendingApprovals: hasAnyAccess,
                hasDocumentApproverAccess: hasDocumentAccess || this.isUserCompanyAdmin(),
                hasCourseApproverAccess: hasCourseAccess || this.isUserCompanyAdmin()
            });
            return hasAnyAccess;
        } catch (error) {
            // If API call fails (unauthorized or error), user is not an approver
            console.error('Error checking user approvals:', error);
            this.setState({
                hasUserPendingApprovals: this.isUserCompanyAdmin(),
                hasDocumentApproverAccess: this.isUserCompanyAdmin(),
                hasCourseApproverAccess: this.isUserCompanyAdmin()
            });
            return false;
        }
    }

    /**
     * Returns whether the user has any approver access (cached value)
     * @returns {boolean} True if user is an approver (for documents or courses)
     */
    isUserApprover = () => {
        return get(this.state, 'hasUserPendingApprovals', false);
    }

    /**
     * Returns whether the user has document approver access (cached value)
     * @returns {boolean} True if user can approve documents
     */
    isDocumentApprover = () => {
        return get(this.state, 'hasDocumentApproverAccess', false);
    }

    /**
     * Returns whether the user has course approver access (cached value)
     * @returns {boolean} True if user can approve course attendance
     */
    isCourseApprover = () => {
        return get(this.state, 'hasCourseApproverAccess', false);
    }

    /**
     * Loads whether any partner client secret is expiring, for the super admin side menu alert
     * @returns {Promise<void>}
     */
    checkExpiringPartnerSecrets = async () => {
        try {
            const {hasExpiringClientSecret} = await SA_getPartnersClientSecretStatus();
            this.setState({hasExpiringPartnerSecret: hasExpiringClientSecret});
        } catch (error) {
            console.error('Error checking partner client secrets:', error);
        }
    }


    render() {
        return <UserReactContext.Provider value={this.state}>
            {this.props.children}
        </UserReactContext.Provider>
    }
}


export const withUserContext = Component => {
    return props => {
        return (
            <UserReactContext.Consumer>
                {(user) => {
                    return <Component {...props} userContext={user}/>;
                }}
            </UserReactContext.Consumer>
        );
    };
};
